"use client";

import {
  MotionConfig,
  motion,
  type Transition,
  useMotionValue,
  useSpring,
  useTransform,
  type Variant,
} from "motion/react";
import { useCallback, useState } from "react";
import { AgoraLogo } from "@/components/custom/svgs/agora-logo";
import { useEvent } from "@/lib/hooks/use-event";
import { cn } from "@/lib/utils";

const springOptions = {
  damping: 24, // Lower = more bouncy
  mass: 0.5, // Lower = faster
  stiffness: 200, // Higher = more rigid
};

const transition: Transition = {
  type: "spring",
  ...springOptions,
};

type CursorType = "hide" | "hovered" | "neutral";

type CursorVariants = Partial<{
  [K in CursorType]: Variant;
}>;

const maskAnimationVariants: CursorVariants = {
  hide: {
    "--mask-size": "0.33%",
    opacity: 0,
  },
  hovered: {
    "--mask-size": "33.33%",
    opacity: 1,
  },
  neutral: {
    "--mask-size": "3.33%",
    opacity: 0.1,
  },
};

const unmaskedLayerAnimationVariants: CursorVariants = {
  hovered: {
    opacity: 0.1,
  },
  neutral: {
    opacity: 1,
  },
};

const formAnimationVariants: CursorVariants = {
  hovered: {
    opacity: 0.3,
  },
  neutral: {
    opacity: 1,
  },
};

export function LoginLayout({
  children,
  hiddenMessage,
}: {
  children: React.ReactNode;
  hiddenMessage?: React.ReactNode;
}) {
  const [cursorType, setCursorType] = useState<CursorType>("neutral");
  const [mounted, setMounted] = useState<boolean>(false);

  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const springX = useSpring(x, springOptions);
  const springY = useSpring(y, springOptions);
  const maskX = useTransform(springX, (latest) => `${latest}px`);
  const maskY = useTransform(springY, (latest) => `${latest}px`);

  // Update cursor position relative to viewport center

  const moveCursor = useCallback(
    function moveCursor(e: MouseEvent) {
      x.set(e.clientX - 0.5 * window.innerWidth);
      y.set(e.clientY - 0.5 * window.innerHeight);
      setMounted(true);
    },
    [x, y],
  );

  useEvent("mousemove", moveCursor);

  return (
    <MotionConfig transition={transition}>
      <main className="bg-background relative overflow-hidden">
        {/* Base Layer */}
        <div className="relative z-20 flex h-[100vh] flex-col items-center justify-center lg:max-w-none lg:flex-row">
          <a
            className="fill-foreground absolute top-4 left-4 lg:top-8 lg:right-8 lg:hidden"
            href="https://www.agora.finance/"
            target="_blank"
            title="Go to Agora Homepage"
            rel="noopener"
          >
            <AgoraLogo className="fill-foreground" height={28} />
            <span className="sr-only">Agora Logo</span>
          </a>

          <div
            className="bg-ausd-green relative hidden h-full basis-[40%] flex-col p-[60px] text-white lg:flex"
            onPointerEnter={() => setCursorType("hovered")}
            onPointerLeave={() => setCursorType("neutral")}
          >
            <motion.div
              animate={cursorType}
              className="relative z-20 flex items-center text-lg font-medium"
              variants={unmaskedLayerAnimationVariants}
            >
              <a href="https://www.agora.finance/" target="_blank" title="Go to Agora Homepage" rel="noopener">
                <AgoraLogo className="fill-background" height={28} />
                <span className="sr-only">Agora Logo</span>
              </a>
            </motion.div>

            <motion.div
              animate={cursorType}
              className="pointer-events-none relative z-20 mt-auto"
              variants={unmaskedLayerAnimationVariants}
            >
              <h1 className="ausd-display-title text-background text-balance">
                Powering the assets and infrastructure of digital finance
              </h1>
            </motion.div>
          </div>

          <motion.div
            animate={cursorType}
            className="flex grow flex-col items-center justify-center lg:p-8"
            variants={formAnimationVariants}
          >
            <div
              className="flex w-full max-w-[400px] flex-col items-center justify-center"
              onPointerEnter={() => setCursorType("hide")}
              onPointerLeave={() => setCursorType("neutral")}
            >
              {children}
            </div>
          </motion.div>
        </div>

        {/* Mask Layer */}

        <motion.div
          animate={cursorType}
          className="mask absolute inset-0 z-30 hidden opacity-0 transition-opacity ease-in-out lg:block"
          style={{
            maskPosition: `${maskX}px ${maskY}px`,
            WebkitMaskPositionX: maskX,
            WebkitMaskPositionY: maskY,
          }}
          variants={maskAnimationVariants}
        >
          <div
            className={cn(
              "bg-ausd-gold relative flex h-[100vh] flex-col items-center justify-center opacity-0 transition-opacity duration-1000 ease-in-out lg:flex-row",
              mounted && "opacity-100",
            )}
          >
            <div className="text-ausd-gold relative hidden h-full basis-[40%] flex-col justify-between bg-black p-[60px] lg:flex">
              <div className="relative z-20 flex items-center text-lg font-medium">
                <a href="https://www.agora.finance/" target="_blank" title="Go to Agora Homepage" rel="noopener">
                  <AgoraLogo className="fill-ausd-gold" height={28} />
                  <span className="sr-only">Agora Logo</span>
                </a>
              </div>

              <div className="relative z-20">
                <div className="ausd-text-base text-ausd-gold pointer-events-none text-[64px] leading-none text-balance">
                  {hiddenMessage ||
                    "The greatest opportunities lie where systems are broken — moving money is no exception."}
                </div>
              </div>

              <div className="h-6"></div>
            </div>

            <div className="flex grow flex-col lg:p-8">
              <div className="flex w-full grow flex-col justify-center">{""}</div>
            </div>
          </div>
        </motion.div>
      </main>
    </MotionConfig>
  );
}
